import { masterDataAtom } from '@/jotai/master.jotai'
import { IInputProperty } from '@/screens/Order/interface/common.interface'
import { PlusOutlined, UploadOutlined } from '@ant-design/icons'
import {
  Form,
  Input,
  DatePicker,
  Select,
  Upload,
  Divider,
  Button,
  Space,
  notification,
  message,
} from 'antd'
import { useAtom } from 'jotai'
import { useEffect, useState } from 'react'
import moment from 'moment'
import './FormItem.style.less'
import useMasterData from '@/hook/MasterData'
import LoadingComponent from '@/components/LoadingComponent/LoadingComponent'
import { onAddNewPropertyValue } from '../../../Order.transform'
import { FIELD_INPUT_TYPE } from '../../../Order.constant'
import { getFieldName } from '../../EditOrder.helper'
import { isEmptyValue } from '@/utils/common'
import { isFieldChangeAtom } from '@/jotai/order.jotai'
import { useAuth } from '@/providers/auth.provider'
import { uniqBy } from 'lodash'

interface Props {
  item: IInputProperty
  orderSetting: any
  disabled?: boolean
  allowLargeFile?: boolean
}

function FormItem(props: Props) {
  const { item } = props
  const [masterData] = useAtom(masterDataAtom)
  const { user } = useAuth()

  const [newSelectInput, setNewSelectInput] = useState('')
  const [selectList, setSelectList] = useState([])
  const [loading, setloading] = useState(false)
  const { refetch } = useMasterData()
  const [, setFieldChange] = useAtom(isFieldChangeAtom)

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e
    }
    return e?.fileList?.map((item) => {
      return { ...item, status: 'done' }
    })
  }

  const getDefaultSelectOptions = () => {
    const data = masterData.get(item.field)
    if (data?.property_values.length > 0) {
      const options = []
      data?.property_values.forEach((element) => {
        options.push({ label: element.value, value: element.value })
      })
      setNewSelectInput('')

      setSelectList(uniqBy(options, 'value'))
    }
  }

  useEffect(() => {
    getDefaultSelectOptions()
  }, [item, masterData])

  const handleCheckAddNewSelect = (text: string = '') => {
    setNewSelectInput(text)
  }

  const handleAddNewSelectOption = async () => {
    setloading(true)
    const response = await onAddNewPropertyValue(item, newSelectInput, user)
    if (response?.data.statusCode == 400) {
      notification.error({
        message: response?.data.message,
        duration: 3,
        placement: 'top',
      })
    }
    await refetch()
    setloading(false)
  }

  const validateFileSize = (value) => {
    if (props.allowLargeFile) {
      if (value.size > 52428800) {
        message.error('Each File cannot be over 50mbps', 3)
        return Upload.LIST_IGNORE
      }
    } else {
      if (value.size > 5242880) {
        message.error('Each File cannot be over 5mbps', 3)
        return Upload.LIST_IGNORE
      }
    }
  }

  return (
    <>
      {loading && <LoadingComponent />}
      {item.data_type == FIELD_INPUT_TYPE.TEXT ? (
        <Form.Item
          name={item.field}
          labelCol={{ span: 6 }}
          labelAlign="left"
          label={
            getFieldName(props?.orderSetting?.field_names, item.field) ??
            item.name
          }
          required={item.required}
          initialValue={item.value}
          rules={
            item.required && [
              {
                required: true,
                message: '',
              },
            ]
          }
        >
          <Input
            onChange={() => setFieldChange(true)}
            placeholder="Text"
            size="large"
          />
        </Form.Item>
      ) : item.data_type == FIELD_INPUT_TYPE.DATETIME ? (
        <Form.Item
          name={item.field}
          labelCol={{ span: 6 }}
          labelAlign="left"
          label={
            getFieldName(props?.orderSetting?.field_names, item.field) ??
            item.name
          }
          required={item.required}
          initialValue={item.value ? moment(item.value) : null}
          rules={
            item.required && [
              {
                required: true,
                message: '',
              },
            ]
          }
        >
          <DatePicker
            onChange={() => setFieldChange(true)}
            placeholder="Date"
            size="large"
            format={'DD MMM YYYY'}
          />
        </Form.Item>
      ) : item.data_type == 'select' ? (
        <Form.Item
          name={item.field}
          labelCol={{ span: 6 }}
          labelAlign="left"
          label={
            getFieldName(props?.orderSetting?.field_names, item.field) ??
            item.name
          }
          required={item.required}
          initialValue={item.value}
          rules={
            item.required && [
              {
                required: true,
                message: '',
              },
            ]
          }
        >
          <Select
            showSearch
            onChange={() => setFieldChange(true)}
            placeholder="Select"
            defaultValue={item.value}
            size="large"
            allowClear
            onSearch={handleCheckAddNewSelect}
            // onBlur={() => setNewSelectInput('')}
            dropdownRender={(menu) => (
              <div
                onMouseDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
              >
                {menu}
                {item.dynamic !== false && (
                  <Space style={{ padding: '0 8px 4px', zIndex: 10 }}>
                    <Divider style={{ margin: '8px 0' }} />
                    <Button
                      disabled={newSelectInput === ''}
                      className="add-new-button"
                      icon={<PlusOutlined className="add-new-button" />}
                      type="text"
                      block
                      onClick={handleAddNewSelectOption}
                    >
                      Add new
                    </Button>
                  </Space>
                )}
              </div>
            )}
            options={selectList}
          />
        </Form.Item>
      ) : item.data_type == 'multi_select' ? (
        <Form.Item
          name={item.field}
          labelCol={{ span: 3 }}
          labelAlign="left"
          label={
            getFieldName(props?.orderSetting?.field_names, item.field) ??
            item.name
          }
          required={item.required}
          initialValue={item?.value || []}
          rules={
            item.required && [
              {
                required: true,
                message: '',
              },
            ]
          }
        >
          <Select
            mode="multiple"
            placeholder="Select"
            showArrow
            defaultValue={item?.value}
            size="large"
            onChange={() => setFieldChange(true)}
            onSearch={handleCheckAddNewSelect}
            dropdownRender={(menu) =>
              item.dynamic !== false && (
                <div
                  onMouseDown={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                >
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <Space style={{ padding: '0 8px 4px', zIndex: 10 }}>
                    <Button
                      onClick={handleAddNewSelectOption}
                      className="add-new-button"
                      icon={<PlusOutlined className="add-new-button" />}
                      type="text"
                      block
                      disabled={newSelectInput === ''}
                    >
                      Add new
                    </Button>
                  </Space>
                </div>
              )
            }
            options={selectList}
          />
        </Form.Item>
      ) : item.data_type == FIELD_INPUT_TYPE.FILE ? (
        <Form.Item
          label={
            getFieldName(props?.orderSetting?.field_names, item.field) ??
            item.name
          }
          name={item.field}
          labelCol={{ span: 6 }}
          initialValue={!isEmptyValue(item.value) ? item.value : []}
          labelAlign="left"
          className="editable-form-order"
          valuePropName="fileList"
          getValueFromEvent={normFile}
        >
          <Upload
            onChange={() => setFieldChange(true)}
            disabled={props.disabled}
            maxCount={10}
            multiple
            accept=".doc,.docx,.docm,.xls,.xlsx,xlsm,.csv,.ppt,.pptx,.ppts,.txt,.rtf,.md,.jpg,.jpeg,.png,.bmp,.tif,.tiff,.svg,.eps,.raw,.psd,.ai,.pdf,.ico,.webp,.gif"
            beforeUpload={validateFileSize}
          >
            <Button
              className="upload-button"
              icon={<UploadOutlined />}
              size="large"
            >
              Select File
            </Button>
          </Upload>
        </Form.Item>
      ) : (
        <Form.Item />
      )}
    </>
  )
}

export default FormItem
